import { Core } from "@faire/design-tokens";
import { useGetFaireAcademyPageState } from "@faire/retailer-visitor-shared/hooks/openWithFaire/useGetFaireAcademyPageState";
import { CHECK_ANIMATION_MS } from "@faire/retailer-visitor-shared/views/open-with-faire/academy/guidePages/components/animationTimingConsts";
import { NegativeFeedbackModal } from "@faire/retailer-visitor-shared/views/open-with-faire/academy/guidePages/components/NegativeFeedbackModal";
import { useGuideStore } from "@faire/retailer-visitor-shared/views/open-with-faire/academy/guidePages/useGuideStore";
import { CheckInCircle } from "@faire/web--source/slate/icons/foundation/CheckInCircle";
import { ThumbsDown } from "@faire/web--source/slate/icons/foundation/ThumbsDown";
import { ThumbsUp } from "@faire/web--source/slate/icons/foundation/ThumbsUp";
import {
  Column,
  HorizontalLine,
  Row,
  VerticalLine,
} from "@faire/web--source/slate/Layout";
import { getSpacing } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { useState } from "@faire/web--source/ui/hooks/useState";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { useMutation as useDislikeChapterMutation } from "@faire/web-api--source/endpoints/www/api/faire-academy/chapterIdentifier/dislike/post-hooks";
import { useMutation as useLikeChapterMutation } from "@faire/web-api--source/endpoints/www/api/faire-academy/chapterIdentifier/like/post-hooks";
import { trackOpenWithFaireFaireAcademyLikedislikeChapterClickClick } from "@faire/web-api--source/events/openWithFaire/click/faireAcademyLikeDislikeChapterClick";
import { IFaireAcademyChapter } from "@faire/web-api--source/indigofair/data/IFaireAcademyChapter";
import React, { useEffect } from "react";
import styled, { css, keyframes } from "styled-components";

type ChapterLikesMap = {
  [key in IFaireAcademyChapter.ChapterIdentifier]?: boolean | undefined;
};

export const ChapterCompleteFeedback: React.FC<{
  isChapterRead: boolean;
  chapterId: IFaireAcademyChapter.ChapterIdentifier;
}> = ({ isChapterRead, chapterId }) => {
  const [{ guide, guideBackendData }] = useGuideStore([
    "guide",
    "guideBackendData",
  ]);
  const chaptersBackendData = guideBackendData?.chapters;
  const { pageState } = useGetFaireAcademyPageState();
  const { mutate: mutateLikeChapter } = useLikeChapterMutation();
  const { mutate: mutateDislikeChapter } = useDislikeChapterMutation();
  const [showNegativeFeedbackModal, setShowNegativeFeedbackModal] =
    useState<boolean>(false);
  const [wasChapterHelpfulMap, setWasChapterHelpfulMap] =
    useState<ChapterLikesMap>({});
  useEffect(() => {
    if (chaptersBackendData === undefined) {
      return;
    }
    const chaptersFeedback: ChapterLikesMap = {};
    chaptersBackendData.forEach((chapter) => {
      if (chapter.identifier) {
        chaptersFeedback[chapter.identifier] = chapter.is_liked;
      }
    });
    setWasChapterHelpfulMap(chaptersFeedback);
  }, [chaptersBackendData]);
  const updateChapterFeedback = (isLiked: boolean) => {
    setWasChapterHelpfulMap((prevFeedback) => ({
      ...prevFeedback,
      [chapterId]: isLiked,
    }));
  };
  const wasChapterHelpful = wasChapterHelpfulMap[chapterId];
  const wasChapterHelpfulClicked = wasChapterHelpful !== undefined;
  const wasChapterHelpfulYes = wasChapterHelpful === true;
  const wasChapterHelpfulNo = wasChapterHelpful === false;

  return (
    <>
      <AppearingColumn gap="2x" align="center" $isVisible={isChapterRead}>
        <Row align="center" gap="3x" style={{ width: "100%" }}>
          <AnimatedHorizontalLine
            color={Core.border.success}
            stroke="2px"
            $transformOrigin="right"
            $playAnimation={isChapterRead}
          />
          <AnimatedCheckInCircle
            height="32px"
            width="32px"
            color={Core.border.success}
            $playAnimation={isChapterRead}
          />
          <AnimatedHorizontalLine
            color={Core.border.success}
            stroke="2px"
            $transformOrigin="left"
            $playAnimation={isChapterRead}
          />
        </Row>
        <Typography variant="pageHeaderSerifRegular">
          <StrictLocalMsg
            defaultMessage="Was this chapter helpful?"
            description={{
              text: "Question asking if the Faire Academy guide chapter about starting a retail business is helpful",
              img: "https://cdn.faire.com/fastly/5ecc6f93e087c560f95bc7986bf549a461099611729ff4ade13fae6a1cf42473.png",
            }}
          />
        </Typography>
        <Row>
          <ButtonRow
            $isSelected={wasChapterHelpfulYes}
            disabled={wasChapterHelpfulYes}
            onClick={() => {
              updateChapterFeedback(true);
              mutateLikeChapter([chapterId]);
              trackOpenWithFaireFaireAcademyLikedislikeChapterClickClick(
                pageState,
                guide.id,
                chapterId,
                "LIKE"
              );
            }}
          >
            <ThumbsUp height="24px" width="24px" />
            <Typography>
              <StrictLocalMsg
                defaultMessage="Yes"
                description={{
                  text: "Selecting yes for question: Was this chapter helpful?",
                  img: "https://cdn.faire.com/fastly/5ecc6f93e087c560f95bc7986bf549a461099611729ff4ade13fae6a1cf42473.png",
                }}
              />
            </Typography>
          </ButtonRow>
          <VerticalLine
            $color={
              wasChapterHelpfulClicked ? Core.border.default : Core.border.muted
            }
            $height="48px"
          />
          <ButtonRow
            $isSelected={wasChapterHelpfulNo}
            disabled={wasChapterHelpfulNo}
            onClick={() => {
              updateChapterFeedback(false);
              mutateDislikeChapter([chapterId]);
              setShowNegativeFeedbackModal(true);
              trackOpenWithFaireFaireAcademyLikedislikeChapterClickClick(
                pageState,
                guide.id,
                chapterId,
                "DISLIKE"
              );
            }}
          >
            <ThumbsDown height="24px" width="24px" />
            <Typography>
              <StrictLocalMsg
                defaultMessage="No"
                description={{
                  text: "Selecting no for question: Was this chapter helpful?",
                  img: "https://cdn.faire.com/fastly/5ecc6f93e087c560f95bc7986bf549a461099611729ff4ade13fae6a1cf42473.png",
                }}
              />
            </Typography>
          </ButtonRow>
        </Row>
      </AppearingColumn>
      {showNegativeFeedbackModal ? (
        <NegativeFeedbackModal
          chapterId={chapterId}
          closeModal={() => {
            setShowNegativeFeedbackModal(false);
          }}
        />
      ) : null}
    </>
  );
};

const AppearingColumn = styled(Column)<{ $isVisible?: boolean }>`
  ${({ $isVisible }) =>
    $isVisible
      ? null
      : css`
          visibility: hidden;
        `}
`;

const ButtonRow = styled.button<{ $isSelected?: boolean }>`
  display: flex;
  gap: ${getSpacing("2x")};
  align-items: center;
  padding: ${getSpacing(10, "4x", 10, "2x")};
  border: 1px solid ${Core.border.muted};
  ${({ $isSelected }) =>
    $isSelected
      ? css`
          border-color: ${Core.border.default};
        `
      : null}
  &:first-child {
    border-right: none;
  }
  &:not(:first-child) {
    border-left: none;
  }
`;

const growLine = keyframes({
  "0%": { transform: "scaleX(0)" },
  "100%": { transform: "scaleX(1)" },
});

const AnimatedHorizontalLine = styled(HorizontalLine)<{
  $transformOrigin: "right" | "left";
  $playAnimation: boolean;
}>`
  transform: scaleX(0);
  ${({ $transformOrigin }) => css`
    transform-origin: ${$transformOrigin};
  `}
  ${({ $playAnimation }) =>
    $playAnimation
      ? css`
          animation: ${growLine} ${CHECK_ANIMATION_MS}ms forwards;
        `
      : null}
`;

const growCircle = keyframes({
  "0%": { transform: "scale(0)" },
  "100%": { transform: "scale(1)" },
});

const AnimatedCheckInCircle = styled(CheckInCircle)<{
  $playAnimation: boolean;
}>`
  transform: scaleX(0);

  ${({ $playAnimation }) =>
    $playAnimation
      ? css`
          animation: ${growCircle} ${CHECK_ANIMATION_MS}ms forwards;
        `
      : null}
`;
